import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Grid, Row, Col, Panel, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import dateFormat from 'dateformat';
import { fetchApi } from '../../Network/NetworkService';
import { exportAsExcel } from '../../Services/exportAsExcel';
import SimpleChart from '../Ui/SimpleChart';

const formatEuros = (amount) => {
    const n = Number(amount) || 0;
    return n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
};

const KIND_LABELS = {
    canceled: 'Annulé',
    notOnline: 'Indisponible à distance'
};

const KIND_LABEL_PLURAL = {
    canceled: 'Annulés',
    notOnline: 'Indisponibles à distance',
    all: 'Tous'
};

const formatPeriodLabel = (period) => {
    const [year, month] = period.split('-');
    const months = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return `${months[parseInt(month, 10) - 1]} ${year}`;
};

const DoctorCancellations = () => {
    const [summary, setSummary] = useState({ months: [], overallByDoctor: [], totalRequests: 0 });
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedMonth, setExpandedMonth] = useState(null);
    const [filters, setFilters] = useState({
        start: '2026-01-01',
        end: new Date().toISOString().split('T')[0],
        doctorId: '',
        kind: 'all'
    });
    const latestFetchRef = useRef(0);

    const fetchData = useCallback(async () => {
        const fetchId = ++latestFetchRef.current;
        setLoading(true);
        setError(null);
        setExpandedMonth(null);
        try {
            const params = new URLSearchParams({
                start: filters.start,
                end: filters.end,
                kind: filters.kind
            });
            if (filters.doctorId) params.append('doctorId', filters.doctorId);

            const [summaryRes, listRes] = await Promise.all([
                fetchApi(`/doctor-cancellations/summary?${params.toString()}`, 'GET'),
                fetchApi(`/doctor-cancellations/list?${params.toString()}`, 'GET')
            ]);

            if (fetchId !== latestFetchRef.current) return;

            const errors = [];
            if (summaryRes && summaryRes.success) {
                setSummary(summaryRes.data);
            } else {
                errors.push((summaryRes && summaryRes.message) || 'résumé');
            }
            if (listRes && listRes.success) {
                setRequests(listRes.data);
            } else {
                errors.push((listRes && listRes.message) || 'liste');
            }
            if (errors.length) setError(`Erreur lors du chargement: ${errors.join(', ')}`);
        } catch (err) {
            if (fetchId !== latestFetchRef.current) return;
            setError(`Erreur: ${err.message}`);
        } finally {
            if (fetchId === latestFetchRef.current) setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const requestsByMonth = useMemo(() => {
        const map = new Map();
        for (const r of requests) {
            const d = new Date(r.createdAt);
            const period = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
            if (!map.has(period)) map.set(period, []);
            map.get(period).push(r);
        }
        return map;
    }, [requests]);

    const netLostByMonth = useMemo(() => {
        const map = new Map();
        for (const [period, rows] of requestsByMonth.entries()) {
            const net = rows.reduce(
                (s, r) => s + (r.wasPaid ? 0 : Number(r.estimatedAmount) || 0),
                0
            );
            map.set(period, net);
        }
        return map;
    }, [requestsByMonth]);

    const totalPaidLost = useMemo(
        () => requests.reduce((s, r) => s + (Number(r.paidAmount) || 0), 0),
        [requests]
    );
    const netLost = useMemo(
        () => requests.reduce(
            (s, r) => s + (r.wasPaid ? 0 : Number(r.estimatedAmount) || 0),
            0
        ),
        [requests]
    );
    const paidCount = useMemo(
        () => requests.filter(r => r.wasPaid).length,
        [requests]
    );

    const monthlyChartData = useMemo(() => {
        if (!summary.months.length) return null;
        const chronological = [...summary.months].sort((a, b) =>
            a.period < b.period ? -1 : 1
        );
        const series = filters.kind === 'all'
            ? [
                { name: 'Annulations', data: chronological.map(m => m.canceledCount || 0) },
                { name: 'Indisponibles à distance', data: chronological.map(m => m.notOnlineCount || 0) }
              ]
            : [{
                name: KIND_LABEL_PLURAL[filters.kind] || 'Total',
                data: chronological.map(m => m.total)
              }];
        return {
            categories: chronological.map(m => formatPeriodLabel(m.period)),
            series
        };
    }, [summary.months, filters.kind]);

    const topDoctorsChartData = useMemo(() => {
        if (!summary.overallByDoctor.length) return null;
        const top = summary.overallByDoctor.slice(0, 15);
        const series = filters.kind === 'all'
            ? [
                { name: 'Annulations', data: top.map(d => d.canceledCount || 0) },
                { name: 'Indisponibles à distance', data: top.map(d => d.notOnlineCount || 0) }
              ]
            : [{
                name: KIND_LABEL_PLURAL[filters.kind] || 'Total',
                data: top.map(d => d.total)
              }];
        return {
            categories: top.map(d => d.doctorName || 'Inconnu'),
            series
        };
    }, [summary.overallByDoctor, filters.kind]);

    const handleExport = () => {
        const rows = requests.map(r => ({
            'Date': dateFormat(r.createdAt, 'dd/mm/yyyy HH:MM'),
            'Type abandon': KIND_LABELS[r.kind] || r.kind,
            'Médecin': r.doctorName || 'Inconnu',
            'Patient': r.userName || 'Inconnu',
            'Email patient': r.userEmail || '',
            'Type': r.type || '',
            'Priorité': r.priority ? 'oui' : 'non',
            'Skycall voice': r.skycallVoice ? 'oui' : 'non',
            'Payé (€)': Number(r.paidAmount) || 0,
            'Manque à gagner estimé (€)': Number(r.estimatedAmount) || 0,
            'Raison (notOnline)': r.notOnlineReason || '',
            'Request ID': r._id
        }));
        exportAsExcel(rows, 'doctor_cancellations');
    };

    const totalAbandons = summary.months.reduce((s, m) => s + m.total, 0);
    const abandonRate = summary.totalRequests > 0
        ? (totalAbandons / summary.totalRequests) * 100
        : 0;

    return (
        <Grid fluid>
            <Row>
                <Col xs={12}>
                    <h2>Abandons par les médecins</h2>
                    <p className="text-muted">
                        Requêtes annulées (<code>state=canceled</code>) ou marquées
                        impossibles à distance (<code>notOnlineReason</code>) par un médecin, regroupées par mois.
                    </p>
                </Col>
            </Row>

            {error && (
                <Row className="mb-3">
                    <Col xs={12}>
                        <div className="alert alert-danger alert-dismissible" role="alert">
                            <button type="button" className="close" onClick={() => setError(null)}>
                                <span>&times;</span>
                            </button>
                            {error}
                        </div>
                    </Col>
                </Row>
            )}

            <Row className="mb-4">
                <Col md={12}>
                    <div className="form-group">
                        <label style={{ marginRight: '15px' }}>Type d'abandon :</label>
                        {[
                            { value: 'all', label: 'Tous' },
                            { value: 'canceled', label: 'Annulations' },
                            { value: 'notOnline', label: 'Indisponibles à distance' }
                        ].map(opt => (
                            <label key={opt.value} style={{ marginRight: '20px', fontWeight: 'normal' }}>
                                <input
                                    type="radio"
                                    name="kind"
                                    value={opt.value}
                                    checked={filters.kind === opt.value}
                                    onChange={(e) => setFilters({ ...filters, kind: e.target.value })}
                                    style={{ marginRight: '5px' }}
                                />
                                {opt.label}
                            </label>
                        ))}
                    </div>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={3}>
                    <div className="form-group">
                        <label>Date début</label>
                        <input
                            type="date"
                            className="form-control"
                            value={filters.start}
                            onChange={(e) => setFilters({ ...filters, start: e.target.value })}
                        />
                    </div>
                </Col>
                <Col md={3}>
                    <div className="form-group">
                        <label>Date fin</label>
                        <input
                            type="date"
                            className="form-control"
                            value={filters.end}
                            onChange={(e) => setFilters({ ...filters, end: e.target.value })}
                        />
                    </div>
                </Col>
                <Col md={3}>
                    <div className="form-group">
                        <label>Filtrer par médecin</label>
                        <select
                            className="form-control"
                            value={filters.doctorId}
                            onChange={(e) => setFilters({ ...filters, doctorId: e.target.value })}
                        >
                            <option value="">Tous les médecins</option>
                            {summary.overallByDoctor.map(d => (
                                <option key={d.doctorId} value={d.doctorId}>
                                    {d.doctorName} ({d.total})
                                </option>
                            ))}
                        </select>
                    </div>
                </Col>
                <Col md={3}>
                    <div className="form-group" style={{ paddingTop: '25px' }}>
                        <Button
                            bsStyle="primary"
                            onClick={fetchData}
                            disabled={loading}
                            style={{ marginRight: '10px' }}
                        >
                            {loading ? 'Chargement...' : 'Actualiser'}
                        </Button>
                        <Button bsStyle="success" onClick={handleExport} disabled={!requests.length}>
                            Exporter Excel
                        </Button>
                    </div>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={3}>
                    <Panel>
                        <Panel.Body>
                            <h5 className="text-muted" style={{ marginTop: 0 }}>Total abandons</h5>
                            <h2 style={{ color: '#d9534f', margin: 0 }}>{totalAbandons}</h2>
                            <small className="text-muted">sur {summary.totalRequests || 0} requêtes</small>
                        </Panel.Body>
                    </Panel>
                </Col>
                <Col md={3}>
                    <Panel>
                        <Panel.Body>
                            <h5
                                className="text-muted"
                                style={{ marginTop: 0 }}
                                title="Total abandons / total des requêtes créées sur la période (état ≠ incomplete). Si un médecin est sélectionné, le ratio est restreint à ses requêtes."
                            >
                                Taux d'abandon <i className="zmdi zmdi-info-outline" />
                            </h5>
                            <h2 style={{ color: '#d9534f', margin: 0 }}>{abandonRate.toFixed(2)}%</h2>
                        </Panel.Body>
                    </Panel>
                </Col>
                <Col md={3}>
                    <Panel>
                        <Panel.Body>
                            <h5 className="text-muted" style={{ marginTop: 0 }}>Médecins concernés</h5>
                            <h2 style={{ margin: 0 }}>{summary.overallByDoctor.length}</h2>
                        </Panel.Body>
                    </Panel>
                </Col>
                <Col md={3}>
                    <Panel>
                        <Panel.Body>
                            <h5
                                className="text-muted"
                                style={{ marginTop: 0 }}
                                title="CA non réalisé = somme des tarifs estimés des requêtes abandonnées qui n'ont pas été encaissées (paidAmount = 0). Tarif estimé = prix de base par type + bonus priorité/skycallVoice."
                            >
                                CA non réalisé <i className="zmdi zmdi-info-outline" />
                            </h5>
                            <h2 style={{ color: '#d9534f', margin: 0 }}>{formatEuros(netLost)}</h2>
                            <small className="text-muted">
                                {paidCount > 0
                                    ? <>+ {formatEuros(totalPaidLost)} déjà encaissés à rembourser ({paidCount} req.)</>
                                    : 'aucune req. encaissée'}
                            </small>
                        </Panel.Body>
                    </Panel>
                </Col>
            </Row>

            {monthlyChartData && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading><h4>Évolution mensuelle des annulations</h4></Panel.Heading>
                            <Panel.Body>
                                <SimpleChart
                                    type="bar"
                                    height={300}
                                    categories={monthlyChartData.categories}
                                    series={monthlyChartData.series}
                                />
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            {topDoctorsChartData && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading><h4>Top 15 médecins par annulations</h4></Panel.Heading>
                            <Panel.Body>
                                <SimpleChart
                                    type="bar"
                                    height={350}
                                    categories={topDoctorsChartData.categories}
                                    series={topDoctorsChartData.series}
                                />
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            <Row>
                <Col xs={12}>
                    <Panel>
                        <Panel.Heading><h4>Détail par mois</h4></Panel.Heading>
                        <Panel.Body>
                            {summary.months.length === 0 && !loading && (
                                <p className="text-muted">Aucune annulation sur cette période.</p>
                            )}
                            {summary.months.map((month) => {
                                const isOpen = expandedMonth === month.period;
                                const monthRequests = requestsByMonth.get(month.period) || [];
                                return (
                                    <div key={month.period} style={{ marginBottom: '15px', border: '1px solid #ddd', borderRadius: '4px' }}>
                                        <div
                                            style={{
                                                padding: '12px 15px',
                                                background: '#f5f5f5',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                            onClick={() => setExpandedMonth(isOpen ? null : month.period)}
                                        >
                                            <div>
                                                <i className={`zmdi zmdi-chevron-${isOpen ? 'down' : 'right'}`} style={{ marginRight: '8px' }} />
                                                <strong>{formatPeriodLabel(month.period)}</strong>
                                                <span className="label label-danger" style={{ marginLeft: '12px' }}>
                                                    {month.total} abandon{month.total > 1 ? 's' : ''}
                                                </span>
                                                {month.totalRequests > 0 && (
                                                    <span
                                                        className="text-muted"
                                                        style={{ marginLeft: '8px' }}
                                                        title={`${month.total} / ${month.totalRequests} requêtes du mois`}
                                                    >
                                                        ({((month.total / month.totalRequests) * 100).toFixed(1)}%)
                                                    </span>
                                                )}
                                                {filters.kind === 'all' && (
                                                    <span className="text-muted" style={{ marginLeft: '12px' }}>
                                                        {month.canceledCount || 0} annul. + {month.notOnlineCount || 0} indispo.
                                                    </span>
                                                )}
                                                <span className="text-muted" style={{ marginLeft: '12px' }}>
                                                    {month.byDoctor.length} médecin{month.byDoctor.length > 1 ? 's' : ''}
                                                </span>
                                                <span
                                                    style={{ marginLeft: '12px', color: '#d9534f', fontWeight: 'bold' }}
                                                    title="CA non réalisé pour ce mois : tarifs estimés des requêtes abandonnées non encaissées."
                                                >
                                                    {formatEuros(netLostByMonth.get(month.period) || 0)} non réalisés
                                                </span>
                                            </div>
                                        </div>
                                        {isOpen && (
                                            <div style={{ padding: '15px' }}>
                                                <h5>Répartition par médecin</h5>
                                                <table className="table table-condensed table-striped" style={{ marginBottom: '20px' }}>
                                                    <thead>
                                                        <tr>
                                                            <th>Médecin</th>
                                                            {filters.kind === 'all' && <th style={{ textAlign: 'right' }}>Annulations</th>}
                                                            {filters.kind === 'all' && <th style={{ textAlign: 'right' }}>Indispo.</th>}
                                                            <th style={{ textAlign: 'right' }}>Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {month.byDoctor.map(d => (
                                                            <tr key={d.doctorId}>
                                                                <td>
                                                                    <Link to={`/doctors/detail/${d.doctorId}`}>
                                                                        {d.doctorName || 'Inconnu'}
                                                                    </Link>
                                                                </td>
                                                                {filters.kind === 'all' && (
                                                                    <td style={{ textAlign: 'right' }}>
                                                                        <span className="label label-danger">{d.canceledCount || 0}</span>
                                                                    </td>
                                                                )}
                                                                {filters.kind === 'all' && (
                                                                    <td style={{ textAlign: 'right' }}>
                                                                        <span className="label label-warning">{d.notOnlineCount || 0}</span>
                                                                    </td>
                                                                )}
                                                                <td style={{ textAlign: 'right' }}>
                                                                    <strong>{d.count}</strong>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>

                                                <h5>Requêtes annulées ({monthRequests.length})</h5>
                                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                    <table className="table table-striped table-bordered table-hover">
                                                        <thead>
                                                            <tr>
                                                                <th>Date</th>
                                                                <th>Type abandon</th>
                                                                <th>Médecin</th>
                                                                <th>Patient</th>
                                                                <th>Type</th>
                                                                <th title="Tarif réel si encaissé, sinon estimé d'après le type + bonus priorité/skycallVoice">
                                                                    Manque à gagner
                                                                </th>
                                                                <th>Raison</th>
                                                                <th>Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {monthRequests.map(r => (
                                                                <tr key={r._id}>
                                                                    <td>{dateFormat(r.createdAt, 'dd/mm/yyyy HH:MM')}</td>
                                                                    <td>
                                                                        <span className={`label label-${r.kind === 'canceled' ? 'danger' : 'warning'}`}>
                                                                            {KIND_LABELS[r.kind] || r.kind}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        {r.doctorId ? (
                                                                            <Link to={`/doctors/detail/${r.doctorId}`}>
                                                                                {r.doctorName || 'Inconnu'}
                                                                            </Link>
                                                                        ) : (
                                                                            <span>{r.doctorName || 'Inconnu'}</span>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        {r.userId ? (
                                                                            <Link to={`/users/detail/${r.userId}`}>
                                                                                {r.userName || r.userEmail || 'Patient'}
                                                                            </Link>
                                                                        ) : (
                                                                            r.userName || '—'
                                                                        )}
                                                                    </td>
                                                                    <td>{r.type}</td>
                                                                    <td>
                                                                        {formatEuros(r.estimatedAmount)}
                                                                        {r.wasPaid ? (
                                                                            <span
                                                                                className="label label-success"
                                                                                style={{ marginLeft: '6px' }}
                                                                                title={`Encaissé : ${formatEuros(r.paidAmount)}`}
                                                                            >
                                                                                payé
                                                                            </span>
                                                                        ) : (
                                                                            <span
                                                                                className="label label-default"
                                                                                style={{ marginLeft: '6px' }}
                                                                                title="Tarif estimé : prix de base du type + bonus priorité/skycallVoice"
                                                                            >
                                                                                estimé
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        {r.notOnlineReason
                                                                            ? <small>{r.notOnlineReason}</small>
                                                                            : <small className="text-muted">—</small>}
                                                                    </td>
                                                                    <td>
                                                                        <Link
                                                                            to={`/requests/detail/${r._id}`}
                                                                            className="btn btn-info btn-xs"
                                                                        >
                                                                            Voir
                                                                        </Link>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </Panel.Body>
                    </Panel>
                </Col>
            </Row>
        </Grid>
    );
};

export default DoctorCancellations;
